import { useRouter } from "next/router";
import { Box, Button, Divider, Typography } from "@mui/material";
import DialogBasic from "@/components/dialog/Dialog";
import PhoneNotificationsSettings from "@/components/push-notifications/PhoneNotificationsSettings";
import { isOrtexApp } from "@/lib/utils";

interface Props {
    open: boolean;
    onClose: () => void;
}

/**
 * "Notification settings", opened from the bell menu (ortex/ted/ted#2485). One place for
 * what reaches the user and how: the phone's push switches (app only), plus the way in
 * to the two settings that live with their content: delivery per alert, and push per
 * Market Intelligence ticker.
 */
const NotificationSettingsDialog = ({ open, onClose }: Props) => {
    const router = useRouter();
    const inApp = isOrtexApp();

    const go = (href: string) => {
        onClose();
        router.push(href);
    };

    return (
        <DialogBasic isOpen={open} onClose={onClose} title="Notification settings" maxWidth="sm">
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
                <Box>
                    <Typography variant="h3" sx={{ mb: 1 }}>
                        Phone notifications
                    </Typography>
                    {inApp ? (
                        <PhoneNotificationsSettings />
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            Push notifications go to the ORTEX app on your phone. Open this menu in the app to choose which ones you get: your alerts, the weekly news and offers.
                        </Typography>
                    )}
                </Box>
                <Divider />
                <Box>
                    <Typography variant="h3" sx={{ mb: 0.5 }}>
                        Alerts
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Each alert has its own delivery: in app (the bell, and your phone in the ORTEX app), email, or both. Set it when you create or edit the alert.
                    </Typography>
                    <Button variant="outlined" size="small" onClick={() => go("/alerts")}>
                        Manage alerts
                    </Button>
                </Box>
                <Divider />
                <Box>
                    <Typography variant="h3" sx={{ mb: 0.5 }}>
                        Ticker pulses
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Follow tickers in Market Intelligence. In-app pulses land in the bell and, in the ORTEX app, on your phone.
                    </Typography>
                    <Button variant="outlined" size="small" onClick={() => go("/market-intelligence?tab=PULSES")}>
                        Market Intelligence pulses
                    </Button>
                </Box>
            </Box>
        </DialogBasic>
    );
};

export default NotificationSettingsDialog;
